"use client";

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Modal from '@/components/ui/Modal';

interface ClinicianApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ClinicianApplicationModal({ isOpen, onClose }: ClinicianApplicationModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    license: '',
    specialty: '',
    preferredLocation: '',
    resume: null as File | null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({
        ...formData,
        resume: e.target.files[0],
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare form data with resume file name if available
      const submissionData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        license: formData.license,
        specialty: formData.specialty,
        preferredLocation: formData.preferredLocation,
        resumeFileName: formData.resume ? formData.resume.name : null,
      };

      const response = await fetch('/api/clinician-application', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);

        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            license: '',
            specialty: '',
            preferredLocation: '',
            resume: null,
          });
          onClose();
        }, 3000);
      } else {
        alert(result.message || 'Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setIsSubmitted(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isSubmitted ? '' : 'Apply for Jobs'}>
      {isSubmitted ? (
        <div className="py-8 px-4 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-[#4fc589]/20 to-[#3a4d46]/40 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#4fc589]/20">
            <Icon name="CheckCircleIcon" size={32} className="text-[#5dd39e]" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Application Received!</h3>
          <p className="text-base text-[#a8c4b8] leading-relaxed">
            A recruiter will call you within 24 hours.
          </p>
        </div>
      ) : (
        <div className="pb-2">
          <div className="text-center mb-4">
            <p className="text-sm text-[#a8c4b8] leading-relaxed">
              Takes 2 minutes • We call within 24 hours
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-1">
                <label htmlFor="name" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="Jane Smith"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="email" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="jane@email.com"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="phone" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="555-123-4567"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="license" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  License Type *
                </label>
                <select
                  id="license"
                  name="license"
                  required
                  value={formData.license}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 shadow-inner appearance-none cursor-pointer"
                >
                  <option value="" className="bg-[#1a2420] text-[#a8c4b8]">Select license</option>
                  <option value="RN" className="bg-[#1a2420]">RN</option>
                  <option value="LPN" className="bg-[#1a2420]">LPN</option>
                  <option value="CNA" className="bg-[#1a2420]">CNA</option>
                  <option value="RT" className="bg-[#1a2420]">Respiratory Therapist</option>
                  <option value="rad-tech" className="bg-[#1a2420]">Rad Tech</option>
                  <option value="surgical-tech" className="bg-[#1a2420]">Surgical Tech</option>
                  <option value="lab-tech" className="bg-[#1a2420]">Lab Tech</option>
                  <option value="NP" className="bg-[#1a2420]">Nurse Practitioner</option>
                  <option value="PA" className="bg-[#1a2420]">Physician Assistant</option>
                  <option value="CRNA" className="bg-[#1a2420]">CRNA</option>
                  <option value="MD-DO" className="bg-[#1a2420]">MD/DO</option>
                </select>
              </div>

              <div className="space-y-1">
                <label htmlFor="specialty" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Specialty *
                </label>
                <input
                  type="text"
                  id="specialty"
                  name="specialty"
                  required
                  value={formData.specialty}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="ICU, ER, Med Surg, etc."
                />
              </div>

              <div className="md:col-span-2 space-y-1">
                <label htmlFor="preferredLocation" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Preferred Location *
                </label>
                <input
                  type="text"
                  id="preferredLocation"
                  name="preferredLocation"
                  required
                  value={formData.preferredLocation}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="City, State or Region"
                />
              </div>

              <div className="md:col-span-2 space-y-1">
                <label htmlFor="resume" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Resume Upload (Optional)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 shadow-inner file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-gradient-to-r file:from-[#3a4d46] file:to-[#4d6359] file:text-[#7ee3b5] hover:file:from-[#4d6359] hover:file:to-[#5dd39e] file:transition-all file:cursor-pointer file:shadow-md"
                  />
                </div>
                <p className="text-xs text-[#a8c4b8] mt-1 ml-1">
                  PDF, DOC, or DOCX • You can also email it later
                </p>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full px-6 py-3 bg-gradient-to-r from-[#3a4d46] to-[#4d6359] text-white text-base font-bold rounded-xl transition-all duration-300 hover:shadow-[0_0_50px_rgba(93,211,158,0.6)] hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center overflow-hidden shadow-lg"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#4d6359] to-[#5dd39e] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="mr-2 animate-spin relative" />
                    <span className="relative">Submitting...</span>
                  </>
                ) : (
                  <>
                    <span className="relative">Submit Application</span>
                    <Icon name="ArrowRightIcon" size={20} className="ml-2 relative group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>

            <p className="text-xs text-[#a8c4b8] text-center -mt-1">
              A recruiter will call within 24 hours to discuss your goals
            </p>
          </form>
        </div>
      )}
    </Modal>
  );
}